import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { translate as translateHOC } from 'react-admin';
import { Typography, FormControl, Input, FormHelperText, withStyles } from '@material-ui/core';
import { MAX_NOTE_LENGTH } from './constants';
import InfoPanel from '../InfoPanel';

const styles = theme => ({
  formControl: {
    width: '100%',
    marginTop: theme.spacing.unit * 2,
    marginBottom: theme.spacing.unit,
  },
  outlinedInput: {
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'}`,
    borderRadius: '4px',
    padding: '16.5px 14px',
    backgroundColor: 'transparent',
    fontSize: '1rem',
    fontFamily: theme.typography.fontFamily,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover': {
      borderColor: theme.palette.text.primary,
    },
    '&:focus': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
    '&.Mui-error': {
      borderColor: theme.palette.error.main,
      '&:focus': {
        borderColor: theme.palette.error.main,
      },
    },
    '& textarea': {
      padding: 0,
      resize: 'vertical',
      overflowX: 'hidden',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      minHeight: '96px',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      borderColor: theme.palette.action.disabled,
    },
  },
  noteText: {
    fontWeight: 400,
    fontStyle: 'Regular',
    fontSize: '16px',
    color: theme.palette.grey[700],
  },
});

const TripNotesField = memo(
  ({ note, error, validationError, handleNoteChange, translate, classes, saving, readOnly }) => {
    if (readOnly) {
      if (!note) {
        return <InfoPanel content={translate('resources.billing.tripNotes.noNotesFound')} type="info" fullWidth />;
      }
      return (
        <Typography variant="body1" className={classes.noteText}>
          {note}
        </Typography>
      );
    }
    return (
      <>
        {error && (
          <Typography color="error" style={{ marginBottom: '16px' }}>
            {error}
          </Typography>
        )}
        <FormControl fullWidth className={classes.formControl} error={!!validationError}>
          <Input
            id="trip-notes-input"
            multiline
            rows={6}
            fullWidth
            value={note}
            onChange={handleNoteChange}
            placeholder={translate('resources.billing.tripNotes.placeholder')}
            disabled={saving}
            disableUnderline
            className={classes.outlinedInput}
            inputProps={{
              'aria-label': translate('resources.billing.tripNotes.label'),
              maxLength: MAX_NOTE_LENGTH,
            }}
          />
          {validationError && <FormHelperText error>{validationError}</FormHelperText>}
          {!validationError && note.length > 0 && (
            <FormHelperText>
              {note.trim().length} / {MAX_NOTE_LENGTH} {translate('common.characters')}
            </FormHelperText>
          )}
        </FormControl>
      </>
    );
  }
);

TripNotesField.propTypes = {
  note: PropTypes.string.isRequired,
  error: PropTypes.string,
  validationError: PropTypes.string,
  handleNoteChange: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  classes: PropTypes.shape({}).isRequired,
  saving: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
};

const enhance = compose(
  translateHOC,
  withStyles(styles)
);

export default enhance(TripNotesField);
